import React, { useEffect, useState, useCallback } from 'react';

// Hooks
import { useDispatch } from 'react-redux';

// Actions
import { success, failure } from '~/redux/actions/snackbar-actions';
import { rescheduleOrderByAdmin } from '~/redux/actions/admin-orders-actions';

// Third Party Libraries
import DatePicker from '~/components/General/OlcDatePicker';
import { addDays, getDay } from 'date-fns';

// Utils
import { MESSAGES } from '~/utils/message';
import { dateProtector } from '~/utils/date-format';
import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

// Mui Components
import Typography from '@mui/material/Typography';
import { Box, CircularProgress, InputLabel } from '@mui/material';

// Icons
import { MultiSelectArrowCIcon } from '~/assets/images';

// Styles
import './styles.scss';

// Mirror of the same helper in Create Order's OrderDetails
const addBusinessDays = (startDate, daysToAdd) => {
  let date = new Date(startDate);
  let daysAdded = 0;
  while (daysAdded < daysToAdd) {
    date.setDate(date.getDate() + 1);
    const dayOfWeek = date.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      daysAdded++;
    }
  }
  return date;
};

const RescheduleOrderModal = (props) => {
  const { openModal, handleCloseModal, selectedOrder, getOrders, isAdmin = false } = props;

  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isDatepickerOpen, setIsDatepickerOpen] = useState(false);

  const dispatch = useDispatch();

  // Filter out weekends and tomorrow — same as Create Order's isWeekday
  const isWeekday = useCallback((date) => {
    const day = getDay(date);
    const tomorrow = addDays(new Date(), 1);
    if (day === 0 || day === 6) return false;
    if (date.toDateString() === tomorrow.toDateString()) return false;
    return true;
  }, []);

  // Reset state on modal open/close
  useEffect(() => {
    if (!openModal) {
      setSelectedDate(null);
      setIsDatepickerOpen(false);
    }
  }, [openModal]);

  const onSubmit = useCallback(async () => {
    try {
      if (!selectedDate) {
        dispatch(failure(MESSAGES.ADMIN_DASHBOARD.ORDERS.RESCHEDULE_ACTION.DATE_REQUIRED));
        return;
      }

      setLoading(true);

      const mailingDate = selectedDate.toLocaleDateString('en-US');
      const payload = { orderId: selectedOrder, mailingDate, isAdmin };
      const response = await dispatch(rescheduleOrderByAdmin(payload));

      if (response?.status === 200) {
        dispatch(success(response?.data?.message || 'Order rescheduled successfully.'));
        getOrders();
        handleCloseModal();
      } else {
        dispatch(failure(response?.data?.message || 'Failed to reschedule order.'));
      }
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || error?.message));
    } finally {
      setTimeout(() => setLoading(false), 3000);
    }
  }, [selectedDate, selectedOrder, isAdmin, dispatch, getOrders, handleCloseModal]);

  return (
    <OlcFormModal
      open={openModal}
      onClose={handleCloseModal}
      title={MESSAGES.ADMIN_DASHBOARD.ORDERS.RESCHEDULE_ACTION?.TITLE}
      className="olcAdminFormModal"
      footer={
        <>
          <OlcButton variant="secondary" onClick={handleCloseModal}>
            {MESSAGES.ADMIN_DASHBOARD.ORDERS.RESCHEDULE_ACTION?.CANCEL_BUTTON}
          </OlcButton>
          <OlcButton variant="primary" onClick={onSubmit} disabled={loading}>
            {loading ? (
              <CircularProgress
                sx={{ color: 'white', width: '25px !important', height: '25px !important' }}
              />
            ) : (
              MESSAGES.ADMIN_DASHBOARD.ORDERS.RESCHEDULE_ACTION?.SUBMIT_BUTTON
            )}
          </OlcButton>
        </>
      }
    >
      <Box className="mailedDate newDatePicker updatedDesign">
        <Box className="selectDate">
          <InputLabel className="dateLabel">
            {MESSAGES.ADMIN_DASHBOARD.ORDERS.RESCHEDULE_ACTION?.RESCHEDULE_DATE_LABEL_NEW}
          </InputLabel>
          <img
            src={MultiSelectArrowCIcon}
            alt="arrow"
            style={{ transform: isDatepickerOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
          <DatePicker
            selected={selectedDate}
            minDate={addBusinessDays(new Date(), 2)}
            filterDate={isWeekday}
            placeholderText={
              MESSAGES.ADMIN_DASHBOARD.ORDERS.RESCHEDULE_ACTION?.RESCHEDULE_DATE_PLACEHOLDER
            }
            dateFormat="MM/dd/yyyy"
            onChange={(date) => {
              dateProtector(date);
              setSelectedDate(date);
            }}
            onFocus={() => setIsDatepickerOpen(true)}
            onCalendarClose={() => setIsDatepickerOpen(false)}
            onBlur={() => setIsDatepickerOpen(false)}
            isClearable
          />
          <Typography className="rescheduleMailDateNote">
            Note: For processing purposes, mailing dates must be scheduled at least 2 days in
            advance. Please select a date later than today and tomorrow.
          </Typography>
        </Box>
      </Box>
    </OlcFormModal>
  );
};

export default RescheduleOrderModal;
